/**
 * Custom Size Pricing Calculator
 * Handles price calculations based on custom dimensions
 */
class CustomSizePricing {
  constructor(config = {}) {
    // Default pricing configuration
    this.config = {
      basePrice: config.basePrice || 10, // Base price per square meter
      minimumPrice: config.minimumPrice || 5, // Minimum order price
      maximumPrice: config.maximumPrice || 1000, // Maximum order price
      currency: config.currency || 'USD',
      priceFormat: config.priceFormat || '${price}',
      tieredPricing: config.tieredPricing || [
        { minArea: 0, maxArea: 1, pricePerSqm: 15 },      // 0-1 m² = $15/m²
        { minArea: 1, maxArea: 5, pricePerSqm: 12 },      // 1-5 m² = $12/m²
        { minArea: 5, maxArea: 10, pricePerSqm: 10 },     // 5-10 m² = $10/m²
        { minArea: 10, maxArea: 999, pricePerSqm: 8 }     // 10+ m² = $8/m²
      ],
      ...config
    };
    
    // Store standard size prices for comparison
    this.standardSizePrices = this.extractStandardPrices();
  }
  
  /**
   * Calculate price based on dimensions in centimeters
   * @param {number} widthCm - Width in centimeters
   * @param {number} heightCm - Height in centimeters
   * @returns {object} Price calculation result
   */
  calculatePrice(widthCm, heightCm) {
    if (!widthCm || !heightCm || widthCm <= 0 || heightCm <= 0) {
      return {
        price: 0,
        formattedPrice: this.formatPrice(0),
        area: 0,
        pricePerSqm: 0,
        error: 'Invalid dimensions'
      };
    }
    
    // Convert cm to meters and calculate area
    const widthM = widthCm / 100;
    const heightM = heightCm / 100;
    const area = widthM * heightM;
    
    // Find the appropriate pricing tier
    const pricingTier = this.findPricingTier(area);
    const pricePerSqm = pricingTier ? pricingTier.pricePerSqm : this.config.basePrice;
    
    // Calculate total price
    let totalPrice = area * pricePerSqm;
    
    // Apply minimum and maximum constraints
    totalPrice = Math.max(totalPrice, this.config.minimumPrice);
    totalPrice = Math.min(totalPrice, this.config.maximumPrice);
    
    return {
      price: totalPrice,
      formattedPrice: this.formatPrice(totalPrice),
      area: area,
      pricePerSqm: pricePerSqm,
      dimensions: {
        width: widthCm,
        height: heightCm,
        widthM: widthM,
        heightM: heightM
      },
      tier: pricingTier
    };
  }
  
  /**
   * Find the appropriate pricing tier for a given area
   * @param {number} area - Area in square meters
   * @returns {object|null} Pricing tier object
   */
  findPricingTier(area) {
    return this.config.tieredPricing.find(tier => 
      area >= tier.minArea && area < tier.maxArea
    ) || this.config.tieredPricing[this.config.tieredPricing.length - 1];
  }
  
  /**
   * Format price according to shop's currency settings
   * @param {number} price - Price to format
   * @returns {string} Formatted price string
   */
  formatPrice(price) {
    // Try to use Shopify's money formatting if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(price * 100); // Shopify uses cents
    }
    
    // Fallback formatting
    return this.config.priceFormat.replace('{price}', price.toFixed(2));
  }
  
  /**
   * Extract standard size prices from the page for comparison
   * @returns {object} Standard size prices
   */
  extractStandardPrices() {
    const prices = {};
    
    try {
      // Try to get product data from window object
      if (typeof window !== 'undefined' && window.productData) {
        window.productData.variants?.forEach(variant => {
          if (variant.option1) { // Assuming first option is size
            prices[variant.option1] = variant.price;
          }
        });
      }
      
      // Fallback: try to extract from DOM
      if (Object.keys(prices).length === 0) {
        const priceElements = document.querySelectorAll('[data-variant-price]');
        priceElements.forEach(el => {
          const size = el.getAttribute('data-size');
          const price = parseFloat(el.getAttribute('data-price'));
          if (size && !isNaN(price)) {
            prices[size] = price;
          }
        });
      }
    } catch (error) {
      console.warn('Could not extract standard size prices:', error);
    }
    
    return prices;
  }
  
  /**
   * Get comparable standard size price
   * @param {number} area - Area in square meters
   * @returns {object|null} Comparable standard size info
   */
  getComparableStandardSize(area) {
    // Define standard sizes with their approximate areas (in square meters)
    const standardSizes = {
      'Small (30x20cm)': 0.06,
      'Medium (50x35cm)': 0.175,
      'Large (70x50cm)': 0.35,
      'Extra Large (100x70cm)': 0.7,
      'XXL (150x100cm)': 1.5
    };
    
    // Find the closest standard size
    let closestSize = null;
    let closestDiff = Infinity;
    
    for (const [sizeName, sizeArea] of Object.entries(standardSizes)) {
      const diff = Math.abs(area - sizeArea);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestSize = { name: sizeName, area: sizeArea };
      }
    }
    
    return closestSize;
  }
  
  /**
   * Generate pricing summary for display
   * @param {number} widthCm - Width in centimeters
   * @param {number} heightCm - Height in centimeters
   * @returns {object} Pricing summary
   */
  getPricingSummary(widthCm, heightCm) {
    const calculation = this.calculatePrice(widthCm, heightCm);
    const comparable = this.getComparableStandardSize(calculation.area);
    
    return {
      ...calculation,
      summary: {
        dimensions: `${widthCm}cm × ${heightCm}cm`,
        area: `${calculation.area.toFixed(2)} m²`,
        pricePerSqm: this.formatPrice(calculation.pricePerSqm),
        comparable: comparable ? `Similar to ${comparable.name}` : null
      }
    };
  }
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CustomSizePricing;
} else if (typeof window !== 'undefined') {
  window.CustomSizePricing = CustomSizePricing;
}
